import { useEffect, useMemo, useRef, useState } from "react";

import { Divider, Flex, Modal, Stack, Switch } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, Text } from "components";
import { ServiceConstants } from "constant";
import { QuickCheckService, UserService } from "services";

import QuickCheckDocumentRow from "./QuickCheckDocumentRow";
import {
  ChangeQuickCheckRequest,
  QuickCheckApiResponse,
  QuickCheckData,
  QuickCheckDocument,
  QuickCheckStatus,
  transformApiResponse,
} from "./types";

const { REQUEST_QUICK_CHECK_LIST_ID } = ServiceConstants;

interface QuickCheckModalProps {
  opened: boolean;
  onClose: () => void;
  userId: number;
  isAdmin?: boolean;
}

const QuickCheckModal = ({ opened, onClose, userId, isAdmin = true }: QuickCheckModalProps) => {
  const { t } = useTranslation();

  // File input refs za svaki tip dokumenta
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Dohvati podatke iz Redux store-a
  const quickCheckListResponse = useSelector(
    (state: any) => state.service.object[REQUEST_QUICK_CHECK_LIST_ID + userId],
  );

  // Transformiši BE response u FE format
  const initialData: QuickCheckData = useMemo(() => {
    const apiDocs: QuickCheckApiResponse[] = quickCheckListResponse?.data || [];
    return transformApiResponse(userId, apiDocs);
  }, [quickCheckListResponse, userId]);

  // Lokalni state za izmene u modalu
  const [localData, setLocalData] = useState<QuickCheckData>(initialData);

  // Sinhronizuj lokalni state kada se promeni initialData
  // Ali samo ako ima pravih podataka (ne resetuj na prazno tokom loading-a)
  useEffect(() => {
    const hasDocuments = initialData.documents.some((doc) => doc.id);
    if (hasDocuments || !localData.documents.some((doc) => doc.id)) {
      setLocalData(initialData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Čuvamo originalne podatke za poređenje pri save-u
  const originalDataRef = useRef<QuickCheckData>(initialData);
  useEffect(() => {
    originalDataRef.current = initialData;
  }, [initialData]);

  const handleStatusChange = (docType: QuickCheckDocument["type"], status: QuickCheckStatus) => {
    setLocalData((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) => (doc.type === docType ? { ...doc, status } : doc)),
    }));
  };

  const handlePointsChange = (docType: QuickCheckDocument["type"], points: number | undefined) => {
    setLocalData((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) => (doc.type === docType ? { ...doc, points } : doc)),
    }));
  };

  const handleBlockedUntilChange = (
    docType: QuickCheckDocument["type"],
    blockedUntil: string | undefined,
  ) => {
    setLocalData((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) =>
        doc.type === docType ? { ...doc, blockedUntil } : doc,
      ),
    }));
  };

  const handleDownload = async (docType: QuickCheckDocument["type"]) => {
    const doc = localData.documents.find((d) => d.type === docType);
    if (doc?.id && doc.fileName && doc.extension) {
      try {
        await QuickCheckService.quickCheckDownload(doc.id, doc.fileName, doc.extension);
      } catch (err) {
        console.error("Download failed:", err);
      }
    }
  };

  const handleView = async (docType: QuickCheckDocument["type"]) => {
    const doc = localData.documents.find((d) => d.type === docType);
    if (doc?.id) {
      try {
        await QuickCheckService.quickCheckView(doc.id);
      } catch (err) {
        console.error("View failed:", err);
      }
    }
  };

  const handleUploadClick = (docType: QuickCheckDocument["type"]) => {
    // Trigger file input click
    fileInputRefs.current[docType]?.click();
  };

  const handleFileChange = async (
    docType: QuickCheckDocument["type"],
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await QuickCheckService.quickCheckUpload(userId, docType, file);
      // Kratka pauza da se Redux store ažurira
      setTimeout(() => {
        QuickCheckService.quickCheckList(userId);
      }, 500);
    } catch (err) {
      console.error("Upload failed:", err);
    }

    // Reset file input
    event.target.value = "";
  };

  const handleDelete = async (docType: QuickCheckDocument["type"]) => {
    const doc = localData.documents.find((d) => d.type === docType);
    if (!doc?.id) return;

    try {
      await QuickCheckService.quickCheckDelete(doc.id);
      // Refresh liste nakon brisanja
      await QuickCheckService.quickCheckList(userId);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleSave = async () => {
    // Pronađi dokumente koji su izmenjeni
    const changedDocs = localData.documents.filter((doc) => {
      if (!doc.id) return false; // Nema dokumenta za update

      const originalDoc = originalDataRef.current.documents.find((d) => d.id === doc.id);
      if (!originalDoc) return false;

      // Proveri da li je nešto promenjeno
      return (
        doc.status !== originalDoc.status ||
        doc.points !== originalDoc.points ||
        doc.blockedUntil !== originalDoc.blockedUntil
      );
    });

    // Pozovi API za svaki izmenjen dokument
    for (const doc of changedDocs) {
      if (!doc.id || !doc.fileName) continue;

      const updateData: ChangeQuickCheckRequest = {
        name: doc.fileName,
        status: doc.status || undefined,
        points: doc.points,
        blockedUntil: doc.blockedUntil,
      };

      try {
        await QuickCheckService.quickCheckUpdate(doc.id, updateData);
      } catch (err) {
        console.error("Update failed for document:", doc.type, err);
      }
    }

    onClose();
  };

  const getDocumentByType = (type: QuickCheckDocument["type"]): QuickCheckDocument => {
    return (
      localData.documents.find((doc) => doc.type === type) || {
        type,
        status: null,
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      centered
      withCloseButton={true}
      title={t("quickCheck.title")}
      padding="xl"
      radius="md"
      styles={{
        title: {
          fontWeight: 600,
          fontSize: 18,
        },
      }}
    >
      {/* Hidden file inputs */}
      {(["BETREIBUNG", "KRIF", "ZEK"] as const).map((type) => (
        <input
          key={type}
          type="file"
          accept=".pdf"
          style={{ display: "none" }}
          ref={(el) => {
            fileInputRefs.current[type] = el;
          }}
          onChange={(e) => handleFileChange(type, e)}
        />
      ))}

      <Stack gap={0}>
        {/* Betreibung */}
        <QuickCheckDocumentRow
          document={getDocumentByType("BETREIBUNG")}
          onStatusChange={(status) => handleStatusChange("BETREIBUNG", status)}
          onBlockedUntilChange={(date) => handleBlockedUntilChange("BETREIBUNG", date)}
          onUpload={() => handleUploadClick("BETREIBUNG")}
          onDownload={() => handleDownload("BETREIBUNG")}
          onView={() => handleView("BETREIBUNG")}
          onDelete={() => handleDelete("BETREIBUNG")}
          isAdmin={isAdmin}
        />

        <Divider my="lg" />

        {/* KRIF */}
        <QuickCheckDocumentRow
          document={getDocumentByType("KRIF")}
          onStatusChange={(status) => handleStatusChange("KRIF", status)}
          onPointsChange={(points) => handlePointsChange("KRIF", points)}
          onUpload={() => handleUploadClick("KRIF")}
          onDownload={() => handleDownload("KRIF")}
          onView={() => handleView("KRIF")}
          onDelete={() => handleDelete("KRIF")}
          isAdmin={isAdmin}
        />

        <Divider my="lg" />

        {/* ZEK */}
        <QuickCheckDocumentRow
          document={getDocumentByType("ZEK")}
          onStatusChange={(status) => handleStatusChange("ZEK", status)}
          onBlockedUntilChange={(date) => handleBlockedUntilChange("ZEK", date)}
          onUpload={() => handleUploadClick("ZEK")}
          onDownload={() => handleDownload("ZEK")}
          onView={() => handleView("ZEK")}
          onDelete={() => handleDelete("ZEK")}
          isAdmin={isAdmin}
        />

        {/* Blacklist toggle - only for admin */}
        {isAdmin && (
          <>
            <Divider my="lg" />
            <Flex align="center" gap="md">
              <Text fw={600}>{t("quickCheck.blacklist")}</Text>
              <Switch
                checked={localData.blacklisted}
                onChange={async () => {
                  try {
                    await UserService.toggleClientBlacklist(userId);
                    // Lokalno ažuriraj state umesto refresh-a (izbegava trepanje UI-ja)
                    setLocalData((prev) => ({ ...prev, blacklisted: !prev.blacklisted }));
                  } catch (err) {
                    console.error("Blacklist toggle failed:", err);
                  }
                }}
                styles={{
                  track: { cursor: "pointer" },
                  thumb: { cursor: "pointer" },
                }}
              />
            </Flex>
          </>
        )}

        {/* Footer buttons - only for admin */}
        {isAdmin && (
          <Flex justify="flex-end" gap="md" mt="xl">
            <Button variant="outline" color="dark" radius="md" onClick={onClose}>
              {t("action.cancel")}
            </Button>
            <Button radius="md" onClick={handleSave}>
              {t("action.save")}
            </Button>
          </Flex>
        )}
      </Stack>
    </Modal>
  );
};

export default QuickCheckModal;
